<script setup lang="ts">
import { ref, watch } from 'vue'

import GlobImage from '@/components/GlobImage.vue'

import type { AppType, CanvasOptions, RulesSet } from '=/types'
import type { Form } from '@/services/form'

const values = defineModel<Form<CanvasOptions>>('values', { required: true })

defineProps<{ rules?: RulesSet; app: AppType }>()

const dropdown = ref<boolean>(false)

const JOINT_FILE_TO_LANG_KEY: Record<string, keyof AppType['lang']['joints']['color']['types']> = {
  wit: 'white',
  witgrijs: 'light_grey',
  cementgrijs: 'grey_cement',
  donkergrijs: 'dark_grey',
  antraciet: 'anthracite',
  zwart: 'black',
  creme: 'cream'
}

function jointTypeLabel(joint: { name: string; file: string }, types: AppType['lang']['joints']['color']['types']): string {
  const slug =
    joint.file
      .replace(/\.[^.]+$/, '')
      .split('_')
      .at(-2) ?? ''
  const key = JOINT_FILE_TO_LANG_KEY[slug]
  return (key && types[key]) ?? joint.name
}

watch(
  () => values.value.form.value.joint.type.name,
  () => (dropdown.value = !dropdown.value),
  { deep: true }
)
</script>

<template>
  <p class="config__text">{{ app.lang.joints.description }}</p>
  <div class="config__form two">
    <p class="title">{{ app.lang.joints.depth.title }}</p>
    <label class="select">
      <input type="radio" name="inset" :value="false" v-model="values.form.value.joint.inset" />
      {{ app.lang.joints.depth.types.flat }}
    </label>
    <label class="select">
      <input type="radio" name="inset" :value="true" v-model="values.form.value.joint.inset" />
      {{ app.lang.joints.depth.types.deep }}
    </label>
  </div>
  <div class="config__form five">
    <p class="title">{{ app.lang.joints.spacing.types.x.title }}</p>
    <template v-for="(x, index) in rules?.joints.spacing.x" :key="index">
      <label class="select">
        <input type="radio" name="spacing.x" :value="x" v-model="values.form.value.joint.spacing.x" />
        {{ x }}mm
      </label>
    </template>
  </div>
  <div class="config__form four">
    <p class="title">{{ app.lang.joints.spacing.types.y.title }}</p>
    <template v-for="(y, index) in rules?.joints.spacing.y" :key="index">
      <label class="select">
        <span v-if="y === rules?.joints.spacing.explicit_y" class="item__label small" :class="{ active: y === values.form.value.joint.spacing.y }">{{
          app.lang.joints.spacing.types.y.recommended
        }}</span>
        <input type="radio" name="spacing.y" :value="y" v-model="values.form.value.joint.spacing.y" />
        {{ y }}mm
      </label>
    </template>
  </div>
  <div class="config__form" v-outside="() => (dropdown = false)">
    <p class="title">{{ app.lang.joints.color.title }}</p>
    <button class="grout-color" @click="dropdown = !dropdown">
      <GlobImage folder="previews" :name="values.form.value.joint.type.file" class="color" />
      {{ values.form.value.joint.type?.file ? jointTypeLabel(values.form.value.joint.type, app.lang.joints.color.types) : app.lang.joints.color.select }}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9.5 5.5">
        <path
          fill="#78716C"
          d="M.22.22a.75.75 0 0 1 1.06 0l3.47 3.469L8.22.219a.75.75 0 0 1 .976-.072L9.28.22a.75.75 0 0 1 0 1.06l-4 4a.75.75 0 0 1-1.06 0l-4-4a.75.75 0 0 1 0-1.06Z"
        />
      </svg>
    </button>
    <div class="dropdown" :class="{ open: dropdown }">
      <div v-if="dropdown">
        <template v-for="(joint, index) in rules?.joints.type" :key="index">
          <label class="dropdown__item">
            <GlobImage class="color" folder="joints" :name="joint.file" />
            {{ jointTypeLabel(joint, app.lang.joints.color.types) }}
            <input type="radio" :id="joint.name" :value="joint" v-model="values.form.value.joint.type" />
          </label>
        </template>
      </div>
    </div>
  </div>
</template>
